import React, { useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';
import { 
  Users, 
  ShoppingBag, 
  Mail,
  Settings, 
  TrendingUp, 
  DollarSign,
  Package,
  MessageSquare,
  BarChart3,
  Plus,
  Download,
  Filter,
  Palette,
  Eye,
  Edit,
  Trash2,
  Power,
  FolderKanban
} from 'lucide-react';
import TableAdmin from '../../components/admin/TableAdmin';
import ModalAdmin from '../../components/admin/ModalAdmin';
import SearchBar from '../../components/admin/SearchBar';
import Pagination from '../../components/admin/Pagination';
import Watermark from '../../components/common/Watermark';

const Dashboard = () => {
    const { theme } = useContext(ThemeContext);
    const { user, isAuthenticated } = useContext(UserContext);
    const isDark = theme === 'dark';

    const [activeTab, setActiveTab] = useState('overview');
    const [userSubTab, setUserSubTab] = useState('users'); // 'users' | 'creators'
    
    // États pour la gestion des utilisateurs
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchUser, setSearchUser] = useState('');
    const [currentPageUsers, setCurrentPageUsers] = useState(1);
    const [totalPagesUsers, setTotalPagesUsers] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [userFormMode, setUserFormMode] = useState('create'); // 'create' | 'edit' | 'view'
    
    // États pour les commandes
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchOrder, setSearchOrder] = useState('');
    const [currentPageOrders, setCurrentPageOrders] = useState(1);
    const [totalPagesOrders, setTotalPagesOrders] = useState(1);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    
    // États pour les messages
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [searchMessage, setSearchMessage] = useState('');
    const [messageTypeFilter, setMessageTypeFilter] = useState('all'); // 'all' | 'contact' | 'conversations'
    const [currentPageMessages, setCurrentPageMessages] = useState(1);
    const [totalPagesMessages, setTotalPagesMessages] = useState(1);
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    // États pour les projets
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [searchProject, setSearchProject] = useState('');
    const [currentPageProjects, setCurrentPageProjects] = useState(1);
    const [totalPagesProjects, setTotalPagesProjects] = useState(1);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectFormMode, setProjectFormMode] = useState('create');
    
    // États pour les produits
    const [products, setProducts] = useState([]);
    const [productsError, setProductsError] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchProduct, setSearchProduct] = useState('');
    const [productStatusTab, setProductStatusTab] = useState('all'); // 'all' | 'pending' | 'active' | 'inactive'
    const [productFilters, setProductFilters] = useState({
      saleType: '',
      status: '',
      creatorType: '',
      creatorId: '',
    });
    const [productSaleType, setProductSaleType] = useState('libre');
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [currentPageProducts, setCurrentPageProducts] = useState(1);
    const [totalPagesProducts, setTotalPagesProducts] = useState(1);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productFormMode, setProductFormMode] = useState('create'); // 'create' | 'edit' | 'view'
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryFormMode, setCategoryFormMode] = useState('create'); // 'create' | 'edit'
    const [selectedCategoryData, setSelectedCategoryData] = useState(null);
    const fileInputRef = useRef(null);
    
    // Résoudre l'URL publique d'une image produit (images relation -> path_watermarked ou path_image via route protégée)
    const resolveProductImageUrl = (p) => {
      if (!p) return null;
      // Construire une liste de candidats dans un ordre robuste
      const candidates = [];
      if (p.image) candidates.push(p.image);
      if (p.path_watermarked) candidates.push(p.path_watermarked);
      if (p.image_url) candidates.push(p.image_url);
      if (p.picture_product) candidates.push(p.picture_product);
      if (Array.isArray(p.images) && p.images.length > 0) {
        const first = p.images[0];
        if (typeof first === 'string') {
          candidates.push(first);
        } else if (first) {
          if (first.path_watermarked) candidates.push(first.path_watermarked);
          if (first.path_image) candidates.push(first.path_image);
        }
      }

      for (let cand of candidates) {
        if (!cand) continue;
        const s = String(cand);
        if (s.startsWith('http')) return s;
        let cleanPath = s.replace(/^\/+/, '');
        if (!cleanPath.includes('/')) {
          const pid = p.id_product || p.id;
          if (pid) cleanPath = `creations/${pid}/${cleanPath}`;
        }
        if (cleanPath.startsWith('storage/')) cleanPath = cleanPath.substring(8);
        return `https://api.krayneagency.fr/api/images/${cleanPath}`;
      }
      return null;
    };
    
    // États pour les créateurs
    const [creators, setCreators] = useState([]);
    const [filteredCreators, setFilteredCreators] = useState([]);
    const [searchCreator, setSearchCreator] = useState('');
    const [currentPageCreators, setCurrentPageCreators] = useState(1);
    const [totalPagesCreators, setTotalPagesCreators] = useState(1);
    const [creatorModalOpen, setCreatorModalOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [creatorFormMode, setCreatorFormMode] = useState('create'); // 'create' | 'edit' | 'view'
    const [creatorsList, setCreatorsList] = useState([]); // Pour le dropdown
    const [uploadedCreatorImage, setUploadedCreatorImage] = useState(null);

    // États pour les gains par créateur
    const [earningsData, setEarningsData] = useState([]);
    const [searchEarnings, setSearchEarnings] = useState('');
    const [filteredEarnings, setFilteredEarnings] = useState([]);
    const [currentPageEarnings, setCurrentPageEarnings] = useState(1);
    const [totalPagesEarnings, setTotalPagesEarnings] = useState(1);

    const itemsPerPage = 10;

    const currentUserId = user?.id_user ?? user?.id;
    const currentUserRole = user?.id_role ?? user?.role_id ?? user?.role?.id_role ?? user?.role;
    const defaultCreatorType = currentUserRole === 3 ? 'krayne' : 'external';
    const defaultCreatorId = currentUserRole === 2 ? currentUserId : null;
    const defaultCreatorName = currentUserRole === 3
      ? 'Krayne Agency'
      : ((user?.nickname ?? `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim()) || 'Créateur');

    // États pour les statistiques
    const [stats, setStats] = useState({
      users: 0,
      orders: 0,
      revenue: 0,
      ursaff: 0,
      messages: 0
    });

    const recentOrders = [
      { id: '#ORD-001', client: 'Jean Dupont', service: 'Site Web', montant: 2500, statut: 'En cours' },
      { id: '#ORD-002', client: 'Marie Martin', service: 'Branding', montant: 1800, statut: 'Complété' },
      { id: '#ORD-003', client: 'Pierre Durand', service: 'Influence TikTok', montant: 3200, statut: 'Nouveau' }
    ];

    const recentUsers = [
      { id: 1, nom: 'Sophie Bernard', email: 'sophie@example.com', role: 'Client', date: '2025-12-26' },
      { id: 2, nom: 'Lucas Petit', email: 'lucas@example.com', role: 'Client', date: '2025-12-25' },
      { id: 3, nom: 'Emma Dubois', email: 'emma@example.com', role: 'Client', date: '2025-12-24' }
    ];
    
    // Charger les statistiques
    useEffect(() => {
      const updateStats = () => {
        const totalRevenue = earningsData.reduce((sum, e) => sum + (e.admin_share || 0), 0);
        const totalUrsaff = earningsData.reduce((sum, e) => sum + (e.ursaff_share || 0), 0);
        
        setStats({
          users: users.length,
          orders: 0, // À compléter avec les vraies données si disponibles
          revenue: totalRevenue,
          ursaff: totalUrsaff,
          messages: messages.length
        });
      };
      
      if (earningsData.length > 0 || users.length > 0 || messages.length > 0) {
        updateStats();
      }
    }, [earningsData, users, messages]);
    
    // Charger les utilisateurs et catégories dès le montage
    useEffect(() => {
      fetchUsers(); // Charger dès le départ pour avoir la liste disponible
      fetchCategories();
      fetchEarnings(); // Charger les gains pour les stats
    }, []);
    
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        let cats = response.data?.data || response.data || [];
        // Si c'est un objet, convertir en array
        if (typeof cats === 'object' && !Array.isArray(cats)) {
          cats = Object.values(cats);
        }
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (error) {
        console.error('Erreur catégories:', error);
        setCategories([]);
      }
    };

    const handleSaveCategory = async (categoryData) => {
      try {
        if (categoryFormMode === 'create') {
          await api.post('/categories', categoryData);
          toast.success('Catégorie créée avec succès');
        } else {
          await api.put(`/categories/${selectedCategoryData.id_category}`, categoryData);
          toast.success('Catégorie modifiée avec succès');
        }
        setCategoryModalOpen(false);
        setSelectedCategoryData(null);
        fetchCategories();
      } catch (error) {
        console.error('Erreur sauvegarde catégorie:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    };

    const handleDeleteCategory = async (categoryId) => {
      if (!window.confirm('Supprimer cette catégorie ? Les produits associés ne seront pas supprimés.')) return;
      try {
        await api.delete(`/categories/${categoryId}`);
        toast.success('Catégorie supprimée');
        fetchCategories();
      } catch (error) {
        console.error('Erreur suppression:', error);
        toast.error(error.response?.data?.message || 'Impossible de supprimer cette catégorie');
      }
    };
    
    useEffect(() => {
      if (activeTab === 'users') {
        fetchUsers();
        fetchCreators();
      }
      if (activeTab === 'earnings') {
        fetchEarnings();
      }
    }, [activeTab]);
    
    const fetchEarnings = async () => {
      try {
        const response = await api.get('/admin/earnings');
        setEarningsData(response.data.earnings || []);
        setFilteredEarnings(response.data.earnings || []);
        const totalP = Math.ceil((response.data.earnings || []).length / itemsPerPage);
        setTotalPagesEarnings(totalP);
      } catch (error) {
        console.error('Erreur chargement gains:', error);
        toast.error('Impossible de charger les gains');
      }
    };
    
    const fetchUsers = async () => {
      try {
        const response = await api.get('/admin/users');
        const raw = response.data?.data || response.data || [];
        const mapped = raw.map((u) => {
          const fullName = `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
          return {
            id: u.id_user ?? u.id,
            id_user: u.id_user ?? u.id,
            nickname: u.nickname ?? '',
            firstname: u.firstname ?? '',
            lastname: u.lastname ?? '',
            name: (u.name ?? fullName) || u.nickname || u.email || '',
            email: u.email ?? '',
            id_role: u.id_role ?? 1,
            role: u.role?.name ?? u.role?.name_role ?? u.role ?? u.role_name ?? '',
            status: u.status ?? 'active',
            email_verified_at: u.email_verified_at,
            picture_user: u.picture_user,
            created_at: u.created_at,
          };
        });
        setUsers(mapped);
        setFilteredUsers(mapped);
        setTotalPagesUsers(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        toast.error('Impossible de charger les utilisateurs');
        setUsers([]);
        setFilteredUsers([]);
      }
    };
    
    // Filtrer les utilisateurs
    // Filtrer les utilisateurs selon le sub-tab actif
    useEffect(() => {
      // Filtrer par rôle selon le sub-tab
      const usersByRole = userSubTab === 'users' 
        ? users.filter(u => u.id_role === 1) // Seulement les utilisateurs (role 1)
        : users; // Tous les users pour le sub-tab créateurs (filtrage fait ailleurs)
      
      const query = searchUser.toLowerCase();
      const filtered = query
        ? usersByRole.filter(u =>
            (u.name || '').toLowerCase().includes(query) ||
            (u.email || '').toLowerCase().includes(query) ||
            (u.nickname || '').toLowerCase().includes(query)
          )
        : usersByRole;
      setFilteredUsers(filtered);
      setCurrentPageUsers(1);
      setTotalPagesUsers(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    }, [searchUser, users, userSubTab, itemsPerPage]);
    
    // Filtrer les gains
    useEffect(() => {
      const query = searchEarnings.toLowerCase();
      const filtered = query
        ? earningsData.filter(e =>
            (e.creator_name || '').toLowerCase().includes(query) ||
            (e.creator_email || '').toLowerCase().includes(query)
          )
        : earningsData;
      setFilteredEarnings(filtered);
      setCurrentPageEarnings(1);
      setTotalPagesEarnings(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    }, [searchEarnings, earningsData]);

    useEffect(() => {
      const query = searchMessage.toLowerCase();
      const filtered = query
        ? messages.filter(m =>
            (m.name || '').toLowerCase().includes(query) ||
            (m.email || '').toLowerCase().includes(query) ||
            (m.subject || '').toLowerCase().includes(query) ||
            (m.message || '').toLowerCase().includes(query)
          )
        : messages;

      setFilteredMessages(filtered);
      setTotalPagesMessages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
      setCurrentPageMessages(1);
    }, [searchMessage, messages]);

    useEffect(() => {
      const query = searchProject.toLowerCase();
      const filtered = query
        ? projects.filter(p =>
            (p.name || '').toLowerCase().includes(query) ||
            (p.user_name || '').toLowerCase().includes(query) ||
            (p.status || '').toLowerCase().includes(query)
          )
        : projects;

      setFilteredProjects(filtered);
      setTotalPagesProjects(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
      setCurrentPageProjects(1);
    }, [searchProject, projects]);
    
    // Charger les commandes
    useEffect(() => {
      if (activeTab === 'orders') {
        fetchOrders();
      }
    }, [activeTab]);
    
    const fetchOrders = async () => {
      try {
        const mockOrders = [
          { id: 1, order_id: '#ORD-001', client: 'Jean Dupont', service: 'Site Web', amount: 2500, status: 'En cours', date: '2025-12-26' },
          { id: 2, order_id: '#ORD-002', client: 'Marie Martin', service: 'Branding', amount: 1800, status: 'Complété', date: '2025-12-25' },
          { id: 3, order_id: '#ORD-003', client: 'Pierre Durand', service: 'Influence TikTok', amount: 3200, status: 'Nouveau', date: '2025-12-24' },
        ];
        setFilteredOrders(mockOrders);
        setTotalPagesOrders(Math.ceil(mockOrders.length / itemsPerPage));
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
      }
    };
    
    // Charger les messages de contact
    useEffect(() => {
      if (activeTab === 'messages') {
        fetchMessages();
      }
    }, [activeTab, messageTypeFilter]);
    
    const fetchMessages = async () => {
      try {
        // Utiliser le nouveau endpoint avec query param
        const endpoint = `/contact/all-messages?message_type=${messageTypeFilter}`;
        
        const response = await api.get(endpoint);
        
        let mapped = [];
        const data = response.data?.data || {};
        
        // Combiner messages de contact et conversations selon le filtre
        if (messageTypeFilter === 'all') {
          // TOUS : Combiner contact_messages + conversations
          const contactMessages = (data.contact_messages || []).map((m) => ({
            id: `contact_${m.id}`,
            type: 'contact',
            name: m.name,
            email: m.email,
            subject: m.service || 'Message de contact',
            message: m.message,
            status: m.status || 'nouveau',
            response: m.response || null,
            response_date: m.response_date || null,
            created_at: m.created_at,
            request_type: m.request_type || 'support',
          }));
          
          const conversations = (data.conversations || []).map((conv) => ({
            id: `conv_${conv.conversation_id}`,
            type: 'conversation',
            name: conv.sender?.name || 'Utilisateur',
            email: conv.sender?.email || 'N/A',
            subject: `Conversation: ${conv.sender?.name} → ${conv.receiver?.name}`,
            message: conv.last_message,
            status: conv.is_closed ? 'clôturé' : 'actif',
            created_at: conv.last_message_date,
            message_count: conv.message_count,
            conversation_id: conv.conversation_id,
          }));
          
          mapped = [...contactMessages, ...conversations];
          
        } else if (messageTypeFilter === 'contact') {
          // FORMULAIRE DE CONTACT : Seulement contact_messages
          mapped = (data.contact_messages || []).map((m) => ({
            id: `contact_${m.id}`,
            type: 'contact',
            name: m.name,
            email: m.email,
            subject: m.service || 'Message de contact',
            message: m.message,
            status: m.status || 'nouveau',
            response: m.response || null,
            response_date: m.response_date || null,
            created_at: m.created_at,
            request_type: m.request_type || 'support',
          }));
          
        } else if (messageTypeFilter === 'conversations') {
          // CONVERSATIONS : Seulement utilisateur → créateur
          mapped = (data.conversations || []).map((conv) => ({
            id: `conv_${conv.conversation_id}`,
            type: 'conversation',
            name: conv.sender?.name || 'Utilisateur',
            email: conv.sender?.email || 'N/A',
            subject: `${conv.sender?.name} → ${conv.receiver?.name}`,
            message: conv.last_message,
            status: conv.is_closed ? 'clôturé' : 'actif',
            created_at: conv.last_message_date,
            message_count: conv.message_count,
            conversation_id: conv.conversation_id,
          }));
        }
        
        // Trier par date décroissante
        mapped.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setMessages(mapped);
        setFilteredMessages(mapped);
        setTotalPagesMessages(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
        
        // Compter les messages non lus
        const unreadCount = mapped.filter(m => m.status === 'nouveau').length;
        setUnreadMessagesCount(unreadCount);
        
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      }
    };

    // Charger les projets
    useEffect(() => {
      if (activeTab === 'projects') {
        fetchProjects();
      }
    }, [activeTab]);

    const fetchProjects = async () => {
      try {
        const response = await api.get('/admin/projects');
        const raw = response.data?.projects || [];
        const mapped = raw.map((p) => ({
          id_project: p.id_project ?? p.id,
          name: p.name,
          type: p.type,
          status: p.status,
          progress: p.progress ?? 0,
          description: p.description,
          start_date: p.start_date,
          end_date: p.end_date,
          updated_at: p.updated_at,
          id_user: p.id_user,
          user_name: p.user ? (p.user.nickname || `${p.user.firstname ?? ''} ${p.user.lastname ?? ''}`.trim() || p.user.email) : p.user_name
        }));
        setProjects(mapped);
        setFilteredProjects(mapped);
        setTotalPagesProjects(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
        setProjects([]);
        setFilteredProjects([]);
      }
    };
    
    // Charger les produits et créateurs
    useEffect(() => {
      if (activeTab === 'products') {
        fetchProducts();
      } else if (activeTab === 'creators') {
        fetchCreators();
      }
    }, [activeTab]);
    
    // Charger la liste des créateurs pour le dropdown
    useEffect(() => {
      fetchCreatorsList();
    }, []);
    
    const fetchCreatorsList = async () => {
      try {
        const response = await api.get('/admin/creators/list');
        const raw = response.data?.data || response.data || [];
        const mapped = raw.map((c) => ({
          id: c.id_user ?? c.id,
          id_user: c.id_user ?? c.id,
          nickname: c.nickname ?? '',
          fullname: `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.nickname || c.email,
          email: c.email ?? '',
        }));
        setCreatorsList(mapped);
      } catch (error) {
        console.error('Erreur lors du chargement des créateurs:', error);
        setCreatorsList([]);
      }
    };
    
    const fetchProducts = async () => {
      try {
        // Admin voit tous les produits
        const response = await api.get(`/admin/products`);
        const raw = response.data?.data || response.data || [];
        const API_URL = 'https://api.krayneagency.fr/';
        const getImageUrl = (img) => {
          if (!img) return null;
          if (img.startsWith('http')) return img.replace('http://localhost', API_URL);
          // Normaliser l'image: retirer les slashes au début et passer par /api/images
          let clean = String(img).replace(/^\/+/, '');
          if (clean.startsWith('storage/')) clean = clean.substring(8);
          return `${API_URL}api/images/${clean}`;
        };
        const mapped = raw.map((p) => {
          const firstImg = Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : null;
          const firstImgPath = typeof firstImg === 'string'
            ? firstImg
            : (firstImg?.path_watermarked || firstImg?.path_image || null);
          // Extraire le nom de catégorie (peut être un objet ou une string)
          const catName = typeof p.category === 'object'
            ? (p.category?.name || p.category?.name_category || '')
            : (p.category || p.category_name || '');
          
          // Debug: afficher les données reçues
          console.log('Produit reçu:', {
            id: p.id_product,
            name: p.name_product,
            stock: p.stock,
            inventory_stock: p.inventory?.stock,
            available_stock: p.available_stock,
            status: p.status
          });
          
          return ({
          id: p.id_product ?? p.id,
          id_product: p.id_product ?? p.id,
          name: p.name ?? p.name_product ?? '',
          category: catName,
          price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          price_ht: p.price ?? p.price_product ?? 0,
          stock: p.inventory?.stock ?? p.stock ?? 0,
          sale_type: p.sale_type ?? '',
          status: p.status ?? '',
          creator_type: p.creator_type ?? '',
          creator_name: p.creator_name ?? '',
          id_user_creator: p.id_user_creator ?? null,
          description: p.description ?? p.description_product ?? '',
          images: p.images ?? [],
          image: getImageUrl(p.image ?? firstImgPath ?? null),
          available_stock: p.inventory?.available_stock ?? p.available_stock ?? p.stock ?? 0,
          price_ttc: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          allowed_users: p.allowed_users ?? [],
          created_at: p.created_at ?? p.createdAt ?? '',
          });
        });
        setProducts(mapped);
        setFilteredProducts(mapped);
        setTotalPagesProducts(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
        setProductsError(null);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setProducts([]);
        setFilteredProducts([]);
        const serverMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
        setProductsError(serverMessage);
      }
    };
    
    // Filtrer les produits (recherche, filtres avancés, visibilité unique)
    useEffect(() => {
      const query = searchProduct.trim().toLowerCase();

      const nextFiltered = products.filter(p => {
        const matchesQuery = query
          ? (p.name || '').toLowerCase().includes(query)
            || (p.category || '').toLowerCase().includes(query)
            || (p.creator_name || '').toLowerCase().includes(query)
          : true;

        const matchesSaleType = productFilters.saleType
          ? (p.sale_type || '').toLowerCase() === productFilters.saleType
          : true;

        // Filtrage par onglet de statut (prioritaire sur le filtre dropdown)
        const matchesStatusTab = productStatusTab === 'all'
          ? true
          : (p.status || '').toLowerCase() === productStatusTab;

        const matchesStatus = productFilters.status
          ? (p.status || '').toLowerCase() === productFilters.status
          : true;

        const matchesCreatorType = productFilters.creatorType
          ? (p.creator_type || '').toLowerCase() === productFilters.creatorType
          : true;

        const matchesCreatorId = productFilters.creatorId
          ? String(p.id_user_creator ?? '') === String(productFilters.creatorId)
          : true;

        // Si produit unique et restreint : indiquer via badge, mais filtrer seulement si créateur sélectionné
        return matchesQuery && matchesSaleType && matchesStatusTab && matchesStatus && matchesCreatorType && matchesCreatorId;
      });

      setFilteredProducts(nextFiltered);
      setTotalPagesProducts(Math.max(1, Math.ceil(nextFiltered.length / itemsPerPage)));
      setCurrentPageProducts(1);
    }, [searchProduct, products, itemsPerPage, productFilters, productStatusTab]);
    
    // Filtrer les créateurs depuis users (role 2)
    useEffect(() => {
      const creatorsFromUsers = users.filter(u => u.id_role === 2);
      
      if (searchCreator.trim() === '') {
        setFilteredCreators(creatorsFromUsers);
      } else {
        const filtered = creatorsFromUsers.filter(c => 
          c.nickname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.firstname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.lastname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchCreator.toLowerCase())
        );
        setFilteredCreators(filtered);
      }
      setTotalPagesCreators(Math.max(1, Math.ceil(filteredCreators.length / itemsPerPage)));
      setCurrentPageCreators(1);
    }, [searchCreator, users, itemsPerPage]);
    
    // Gestionnaires d'événements
    const handleCreateUser = () => {
      setUserFormMode('create');
      setSelectedUser(null);
      setUserModalOpen(true);
    };
    
    const handleEditUser = (user) => {
      setUserFormMode('edit');
      setSelectedUser(user);
      setUserModalOpen(true);
    };
    
    const handleViewUser = (user) => {
      setUserFormMode('view');
      setSelectedUser(user);
      setUserModalOpen(true);
    };
    
    const handleDeleteUser = async (user) => {
      if (window.confirm(`Voulez-vous vraiment supprimer l'utilisateur ${user.nickname} ?`)) {
        try {
          await api.delete(`/admin/users/${user.id_user || user.id}`);
          toast.success('Utilisateur supprimé avec succès');
          fetchUsers();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };
    
    const handleSaveUser = async (userData) => {
      try {
        if (userFormMode === 'create') {
          await api.post('/admin/users', userData);
          toast.success('Utilisateur créé avec succès');
        } else {
          const response = await api.put(`/admin/users/${selectedUser.id_user || selectedUser.id}`, userData);
          toast.success('Utilisateur modifié avec succès');
          
          // Si l'email a changé, afficher un message de notification
          if (response.data?.emailChanged) {
            toast.info('L\'utilisateur devra vérifier son nouvel email pour réactiver son compte', { duration: 5000 });
          }
          
          // Si le statut a changé, afficher un message spécifique
          if (response.data?.statusChanged) {
            if (userData.status === 'inactive') {
              toast.error('Compte utilisateur désactivé - il ne pourra plus se connecter');
            } else {
              toast.success('Compte utilisateur réactivé');
            }
          }
        }
        setUserModalOpen(false);
        fetchUsers();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    };
    
    const handleViewOrder = (order) => {
      setSelectedOrder(order);
      setOrderModalOpen(true);
    };
    
    const handleViewMessage = async (message) => {
      setSelectedMessage(message);
      setReplyText(message?.response || '');
      setMessageModalOpen(true);
      
      // Marquer comme lu si c'est un nouveau message
      if (message.status === 'nouveau') {
        try {
          await api.put(`/contact/messages/${message.id}/mark-read`);
          // Mettre à jour le message localement
          const updatedMessages = messages.map(m => 
            m.id === message.id ? { ...m, status: 'lu' } : m
          );
          setMessages(updatedMessages);
          setFilteredMessages(updatedMessages.filter(m => {
            const query = searchMessage.toLowerCase();
            if (!query) return true;
            return (m.name || '').toLowerCase().includes(query) ||
                   (m.email || '').toLowerCase().includes(query) ||
                   (m.subject || '').toLowerCase().includes(query) ||
                   (m.message || '').toLowerCase().includes(query);
          }));
          // Décrémenter le compteur de non lus
          setUnreadMessagesCount(prev => Math.max(0, prev - 1));
        } catch (error) {
          console.error('Erreur marquage message lu:', error);
        }
      }
    };

    const handleReplyMessage = async () => {
      if (!selectedMessage?.id || !replyText.trim()) return;
      try {
        await api.post(`/contact/messages/${selectedMessage.id}/reply`, { response: replyText.trim() });
        toast.success('Réponse envoyée');
        setMessageModalOpen(false);
        fetchMessages();
      } catch (error) {
        console.error('Erreur réponse message:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la réponse');
      }
    };
    
    const handleDeleteMessage = async (message) => {
      if (window.confirm('Voulez-vous vraiment supprimer ce message ?')) {
        try {
          // await api.delete(`/admin/messages/${message.id}`);
          setMessages(messages.filter(m => m.id !== message.id));
          toast.success('Message supprimé');
        } catch (error) {
          console.error('Erreur:', error);
        }
      }
    };
    
    // Gestionnaires projets
    const handleCreateProject = () => {
      setProjectFormMode('create');
      setSelectedProject({
        id_user: '',
        name: '',
        type: '',
        status: 'En cours',
        progress: 0,
        description: '',
        start_date: '',
        end_date: ''
      });
      setProjectModalOpen(true);
    };

    const handleEditProject = (project) => {
      setProjectFormMode('edit');
      setSelectedProject(project);
      setProjectModalOpen(true);
    };

    const handleViewProject = (project) => {
      setProjectFormMode('view');
      setSelectedProject(project);
      setProjectModalOpen(true);
    };

    const handleDeleteProject = async (project) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le projet "${project.name}" ?`)) {
        try {
          await api.delete(`/admin/projects/${project.id_project || project.id}`);
          toast.success('Projet supprimé');
          fetchProjects();
        } catch (error) {
          console.error('Erreur suppression projet:', error);
          toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };

    const handleSaveProject = async (projectData) => {
      try {
        if (projectFormMode === 'create') {
          await api.post('/admin/projects', projectData);
          toast.success('Projet créé');
        } else {
          await api.put(`/admin/projects/${selectedProject.id_project || selectedProject.id}`, projectData);
          toast.success('Projet modifié');
        }
        setProjectModalOpen(false);
        fetchProjects();
      } catch (error) {
        console.error('Erreur sauvegarde projet:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    };
    
    // Gestionnaires produits
    const handleCreateProduct = () => {
      setProductFormMode('create');
      setSelectedProduct({
        name: '',
        category: '',
        price: '',
        stock: 1,
        sale_type: 'libre',
        status: 'active',
        creator_type: defaultCreatorType,
        id_user_creator: defaultCreatorId,
        creator_name: defaultCreatorName,
        description: '',
      });
      setProductSaleType('libre');
      setSelectedCategory('');
      setAllowedUsers([]);
      setUploadedImage(null);
      setImagePreview(null);
      setProductModalOpen(true);
    };
    
    const handleEditProduct = async (product) => {
      setProductFormMode('edit');
      setUploadedImage(null);
      setImagePreview(null);

      // Charger le produit complet (avec images) pour l'aperçu fiable
      let full = product;
      try {
        const id = product.id_product || product.id;
        const { data } = await api.get(`/products/${id}`);
        full = data?.data || data || product;
      } catch (e) {
        console.warn('Impossible de charger le détail du produit, utilisation des données de la ligne:', e?.message);
      }

      setSelectedProduct(full);
      setProductSaleType(full?.sale_type || 'libre');
      // Extraire le nom de la catégorie (peut être un objet ou une string)
      const catName = typeof full?.category === 'object' 
        ? (full?.category?.name || full?.category?.name_category || '')
        : (full?.category || '');
      setSelectedCategory(catName);
      // Extraire les IDs des utilisateurs autorisés (peut être array d'objets ou array de strings)
      const userIds = (full?.allowed_users || []).map(u => 
        typeof u === 'object' ? String(u.id_user || u.id) : String(u)
      );
      setAllowedUsers(userIds);
      setProductModalOpen(true);

      // Initialiser le calcul TVA après ouverture du modal
      setTimeout(() => {
        const priceInput = document.querySelector('input[name="price"]');
        const priceHT = (() => {
          if (!priceInput) return parseFloat(full?.price_ht) || parseFloat(full?.price) || 0;
          return parseFloat(priceInput.value) || parseFloat(full?.price_ht) || parseFloat(full?.price) || 0;
        })();
        const tva = priceHT * 0.20;
        const priceTTC = priceHT + tva;

        const priceDiv = document.querySelector('.price-calc-display-admin');
        if (priceDiv && priceHT > 0) {
          priceDiv.innerHTML = `
            <div class="text-xs space-y-1.5">
              <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
              <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
              <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
            </div>
          `;
        }
      }, 200);
    };
    
    const handleViewProduct = (product) => {
      // Éviter un fetch qui peut renvoyer 500; utiliser les données présentes
      setProductFormMode('view');
      setSelectedProduct(product);
      setProductSaleType(product?.sale_type || 'libre');
      // Extraire le nom de la catégorie (peut être un objet ou une string)
      const catName = typeof product?.category === 'object' 
        ? (product?.category?.name || product?.category?.name_category || '')
        : (product?.category || '');
      setSelectedCategory(catName);
      // Extraire les IDs des utilisateurs autorisés
      const userIds = (product?.allowed_users || []).map(u => 
        typeof u === 'object' ? String(u.id_user || u.id) : String(u)
      );
      setAllowedUsers(userIds);
      setUploadedImage(null);
      setImagePreview(null);
      setProductModalOpen(true);
    };
    
    const handleDeleteProduct = async (product) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le produit ${product.name} ?`)) {
        try {
          await api.delete(`/admin/products/${product.id_product || product.id}`);
          setProducts(products.filter(p => (p.id_product || p.id) !== (product.id_product || product.id)));
          toast.success('Produit supprimé avec succès');
          fetchProducts();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          toast.error('Erreur lors de la suppression du produit');
        }
      }
    };

    const handleActivateProduct = async (product) => {
      const id = product?.id_product || product?.id;
      if (!id) {
        toast.error('ID du produit manquant pour activer');
        console.error('Activation produit: ID manquant', product);
        return;
      }

      try {
        await api.patch(`/admin/products/${id}/activate`);
        toast.success('Produit activé');
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors de l\'activation:', error);
        toast.error('Impossible d\'activer le produit');
      }
    };

    const handleToggleProductStatus = async (product) => {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      try {
        const id = product.id_product || product.id;
        await api.patch(`/admin/products/${id}/status`, { status: newStatus });
        toast.success(`Produit ${newStatus === 'active' ? 'activé' : 'désactivé'} avec succès`);
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors du basculement du statut:', error);
        toast.error('Erreur lors du basculement du statut');
      }
    };
    
    const handleImageUpload = async (e) => {
      e.stopPropagation();
      const file = e.target.files[0];
      if (file) {
        try {
          console.log('Image sélectionnée:', file.name, 'Taille:', (file.size / 1024 / 1024).toFixed(2), 'MB');
          
          // Options de compression
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          };
          
          // Compresser l'image
          const compressedFile = await imageCompression(file, options);
          console.log('Image compressée:', compressedFile.name, 'Taille:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
          
          // Stocker le fichier compressé
          setUploadedImage(compressedFile);
          
          // Créer un aperçu
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreview(reader.result);
            console.log('Aperçu créé avec succès');
          };
          reader.onerror = () => {
            console.error('Erreur lors de la lecture du fichier');
            toast.error('Erreur lors de la lecture de l\'image');
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Erreur handleImageUpload:', error);
          toast.error('Erreur lors du traitement de l\'image');
        }
      }
    };
    
    // Handlers Créateurs
    const fetchCreators = async () => {
      try {
        const response = await api.get('/admin/creators');
        const raw = response.data?.creators || response.data?.data || response.data || [];
        const mapped = raw.map((c) => ({
          id_user: c.id_user ?? c.id,
          id: c.id_user ?? c.id,
          nickname: c.nickname ?? '',
          firstname: c.firstname ?? '',
          lastname: c.lastname ?? '',
          email: c.email ?? '',
          id_role: c.id_role ?? 2,
          role: c.role ?? c.role_name ?? 'Créateur',
          status: c.status ?? 'active',
          email_verified_at: c.email_verified_at,
          created_at: c.created_at,
        }));
        setCreators(mapped);
      } catch (error) {
        console.error('Erreur lors du chargement des créateurs:', error);
      }
    };
    
    const handleEditCreator = (creator) => {
      setCreatorFormMode('edit');
      setSelectedCreator(creator);
      setUploadedCreatorImage(null);
      setCreatorModalOpen(true);
    };
    
    const handleViewCreator = (creator) => {
      setCreatorFormMode('view');
      setSelectedCreator(creator);
      setCreatorModalOpen(true);
    };
    
    const handleDeleteCreator = async (creator) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le créateur ${creator.nickname} ?`)) {
        try {
          await api.delete(`/admin/creators/${creator.id_user}`);
          toast.success('Créateur supprimé avec succès');
          fetchCreators();
          fetchCreatorsList();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          if (error.response?.status === 409) {
            toast.error('Impossible de supprimer ce créateur car il a des produits associés.');
          } else {
            toast.error('Erreur lors de la suppression du créateur');
          }
        }
      }
    };
    
    const handleSaveCreator = async (creatorData) => {
      try {
        const formData = new FormData();
        formData.append('nickname', creatorData.nickname);
        formData.append('firstname', creatorData.firstname);
        formData.append('lastname', creatorData.lastname);
        formData.append('email', creatorData.email);
        
        if (creatorData.password) {
          formData.append('password', creatorData.password);
        }
        
        if (uploadedCreatorImage) {
          formData.append('picture_user', uploadedCreatorImage);
        }
        
        if (creatorFormMode === 'create') {
          await api.post('/admin/creators', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Créateur créé avec succès!');
        } else {
          await api.put(`/admin/creators/${selectedCreator.id_user}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Créateur modifié avec succès');
        }
        
        setCreatorModalOpen(false);
        setUploadedCreatorImage(null);
        fetchCreators();
        fetchCreatorsList();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        toast.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.message || error.message));
      }
    };
    
    const handleCreatorImageUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        setUploadedCreatorImage(file);
      }
    };
    
    const handleSaveProduct = async (productData) => {
      try {
        // Vérifier l'authentification avant toute chose
        if (!isAuthenticated) {
          toast.error('Vous devez être connecté pour ajouter un produit', {
            duration: 4000,
            icon: '🔒',
            style: {
              background: isDark ? '#1f2937' : '#ffffff',
              color: isDark ? '#ffffff' : '#1f2937',
              border: '2px solid #ef4444',
            }
          });
          return;
        }

        // Validation côté client pour éviter un aller-retour inutile si l'image manque
        if (productFormMode === 'create' && !uploadedImage) {
          toast.error('Veuillez sélectionner une image avant de créer le produit');
          return;
        }

        // Validation des champs numériques
        const priceValue = Number(productData.price);
        const stockValue = Number(productData.stock);
        if (!Number.isFinite(priceValue) || priceValue < 0) {
          toast.error('Le prix doit être un nombre positif');
          return;
        }
        if (!Number.isInteger(stockValue) || stockValue < 0) {
          toast.error('Le stock doit être un entier positif (0 = illimité)');
          return;
        }

        // Validation: vérifier qu'UN utilisateur est sélectionné pour les ventes uniques
        if (productData.sale_type === 'unique' && allowedUsers.length === 0) {
          toast.error('Veuillez sélectionner UN utilisateur pour cette vente unique');
          return;
        }
        if (productData.sale_type === 'unique' && allowedUsers.length > 1) {
          toast.error('Une vente unique ne peut avoir qu\'UN SEUL acheteur');
          return;
        }

        // Pour l'admin, tous les produits sont de Krayne Agency
        const creatorType = 'krayne';
        const creatorName = 'Krayne Agency';

        const formData = new FormData();
        formData.append('name', productData.name);
        formData.append('category', productData.category);
        formData.append('price', priceValue);
        formData.append('stock', stockValue);
        formData.append('sale_type', productData.sale_type);
        formData.append('description', productData.description || '');
        formData.append('status', productData.status);
        formData.append('creator_type', creatorType);
        formData.append('creator_name', creatorName);

        // Produits en vente unique : limiter la visibilité à une liste d'utilisateurs
        if ((productData.sale_type || '').toLowerCase() === 'unique' && allowedUsers.length > 0) {
          allowedUsers.forEach((id) => formData.append('allowed_users[]', id));
        }
        
        if (uploadedImage) {
          formData.append('image', uploadedImage);
        }
        
        if (productFormMode === 'create') {
          await api.post('/admin/products', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('Produit créé avec succès!');
        } else {
          // Laravel ne supporte pas PUT avec multipart/form-data
          // On utilise POST avec _method=PUT
          formData.append('_method', 'PUT');
          await api.post(`/admin/products/${selectedProduct.id_product || selectedProduct.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('Produit modifié avec succès');
        }
        
        setProductModalOpen(false);
        setUploadedImage(null);
        setImagePreview(null);
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error.response?.data || error);
        const serverMessage = error.response?.data?.message || error.message;
        // Si le backend retourne des erreurs de validation, afficher la première
        const validationErrors = error.response?.data?.errors;
        const firstValidationError = validationErrors ? Object.values(validationErrors)[0]?.[0] : null;
        toast.error('Erreur lors de la sauvegarde: ' + (firstValidationError || serverMessage));
      }
    };

    const tabs = [
      { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
      { id: 'users', label: 'Utilisateurs', icon: Users },
      { id: 'earnings', label: 'Gains par Créateur', icon: DollarSign },
      { id: 'products', label: 'Produits', icon: Package },
      { id: 'categories', label: 'Catégories', icon: FolderKanban },
      { id: 'projects', label: 'Projets', icon: FolderKanban },
      { id: 'messages', label: 'Messages', icon: MessageSquare }
    ];

    const StatCard = ({ icon: Icon, title, value, trend, color }) => (
      <div className={`p-6 rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-sm text-rose-500">
              <TrendingUp className="w-4 h-4" />
              <span>{trend}</span>
            </div>
          )}
        </div>
        <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{title}</h3>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    );

    const resolvedCreatorType = selectedProduct?.creator_type || defaultCreatorType;
    const resolvedCreatorId = selectedProduct?.id_user_creator ?? defaultCreatorId;
    const resolvedCreatorName = selectedProduct?.creator_name ?? defaultCreatorName;

    return (
        <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">Administration</h1>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Bienvenue, {user?.name || 'Admin'} • Dernière connexion : Aujourd'hui à 14:32
                    </p>
                </div>

                {/* Tabs Navigation */}
                <div className={`flex gap-2 mb-6 p-1 rounded-lg border overflow-x-auto ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                  {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors select-none relative ${
                          activeTab === tab.id 
                            ? 'bg-rose-500 text-white' 
                            : isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                        {tab.id === 'messages' && unreadMessagesCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadMessagesCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Content */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard 
                        icon={Users} 
                        title="Utilisateurs" 
                        value={stats.users.toLocaleString()} 
                        trend="+12%"
                        color="bg-blue-500/10 text-blue-500"
                      />
                      <StatCard 
                        icon={ShoppingBag} 
                        title="Commandes" 
                        value={stats.orders} 
                        trend="+8%"
                        color="bg-rose-500/10 text-rose-500"
                      />
                      <StatCard 
                        icon={DollarSign} 
                        title="Revenus (Admin)"
                        value={
                          <div className="flex flex-col">
                            <span className="text-white">{stats.revenue.toFixed(2)}€</span>
                            <span className="text-xs text-gray-400 font-normal">({stats.ursaff.toFixed(2)}€ URSSAF)</span>
                          </div>
                        }
                        color="bg-green-500/10 text-green-500"
                      />
                      <StatCard 
                        icon={Mail} 
                        title="Messages" 
                        value={stats.messages} 
                        color="bg-purple-500/10 text-purple-500"
                      />
                    </div>

                    {/* Recent Activity */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Recent Orders */}
                      <div className={`p-6 rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <ShoppingBag className="w-5 h-5 text-rose-500" />
                          Commandes récentes
                        </h2>
                        <div className="space-y-3">
                          {recentOrders.map(order => (
                            <div key={order.id} className={`p-3 rounded-lg border ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} transition-colors cursor-pointer`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm">{order.id}</span>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  order.statut === 'Complété' ? 'bg-green-500/20 text-green-500' :
                                  order.statut === 'En cours' ? 'bg-blue-500/20 text-blue-500' :
                                  'bg-rose-500/20 text-rose-500'
                                }`}>
                                  {order.statut}
                                </span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{order.client} • {order.service}</p>
                              <p className="text-sm font-semibold mt-1">{order.montant}€</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recent Users */}
                      <div className={`p-6 rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-500" />
                          Nouveaux utilisateurs
                        </h2>
                        <div className="space-y-3">
                          {recentUsers.map(user => (
                            <div key={user.id} className={`p-3 rounded-lg border ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} transition-colors cursor-pointer`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{user.nom}</span>
                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{user.date}</span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{user.email}</p>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                                {user.role}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'users' && (
                  <div className="space-y-4">
                    {/* Sub-tabs pour Utilisateurs et Créateurs */}
                    <div className="flex gap-2 mb-6">
                      <button
                        onClick={() => setUserSubTab('users')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          userSubTab === 'users'
                            ? 'bg-rose-500 text-white'
                            : isDark
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Utilisateurs
                      </button>
                      <button
                        onClick={() => setUserSubTab('creators')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          userSubTab === 'creators'
                            ? 'bg-rose-500 text-white'
                            : isDark
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Créateurs
                      </button>
                    </div>

                    {/* Contenu Utilisateurs */}
                    {userSubTab === 'users' && (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-xl font-semibold">Gestion des utilisateurs</h2>
                          <button 
                            onClick={handleCreateUser}
                            className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Nouvel utilisateur
                          </button>
                        </div>
                        
                        <div className="mb-4">
                          <SearchBar 
                            value={searchUser}
                            onChange={setSearchUser}
                            placeholder="Rechercher un utilisateur..."
                          />
                        </div>
                        
                        <TableAdmin
                          columns={[
                            { key: 'id_user', label: 'ID' },
                            { key: 'nickname', label: 'Pseudo' },
                            { key: 'email', label: 'Email' },
                            { 
                              key: 'id_role', 
                              label: 'Rôle',
                              render: (role) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  role === 3 ? 'bg-purple-500/20 text-purple-500' :
                                  role === 2 ? 'bg-blue-500/20 text-blue-500' :
                                  'bg-gray-500/20 text-gray-500'
                                }`}>
                                  {role === 3 ? 'Admin' : role === 2 ? 'Créateur' : 'Utilisateur'}
                                </span>
                              )
                            },
                            { 
                              key: 'email_verified_at', 
                              label: 'Vérification Email',
                              render: (verified) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  verified ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'
                                }`}>
                                  {verified ? '✓ Vérifié' : '⏳ En attente'}
                                </span>
                              )
                            },
                            { 
                              key: 'status', 
                              label: 'Statut',
                              render: (status) => (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                              )
                            },
                            { key: 'created_at', label: 'Date inscription' }
                          ]}
                          data={filteredUsers.slice((currentPageUsers - 1) * itemsPerPage, currentPageUsers * itemsPerPage)}
                          onView={handleViewUser}
                          onEdit={handleEditUser}
                          onDelete={handleDeleteUser}
                        />
                        
                        <Pagination
                          currentPage={currentPageUsers}
                          totalPages={totalPagesUsers}
                          onPageChange={setCurrentPageUsers}
                        />
                      </>
                    )}

                    {/* Contenu Créateurs */}
                    {userSubTab === 'creators' && (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-xl font-semibold">Gestion des créateurs</h2>
                          <button 
                            onClick={() => {
                              setSelectedCreator(null);
                              setCreatorFormMode('create');
                              setCreatorModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Ajouter un créateur
                          </button>
                        </div>
                        
                        <div className="mb-4">
                          <SearchBar 
                            value={searchCreator}
                            onChange={setSearchCreator}
                            placeholder="Rechercher un créateur..."
                          />
                        </div>
                        
                        <TableAdmin
                          columns={[
                            { key: 'id_user', label: 'ID' },
                            { key: 'nickname', label: 'Pseudo' },
                            { key: 'email', label: 'Email' },
                            { 
                              key: 'id_role', 
                              label: 'Rôle',
                              render: (role) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  role === 3 ? 'bg-purple-500/20 text-purple-500' :
                                  role === 2 ? 'bg-blue-500/20 text-blue-500' :
                                  'bg-gray-500/20 text-gray-500'
                                }`}>
                                  {role === 3 ? 'Admin' : role === 2 ? 'Créateur' : 'Utilisateur'}
                                </span>
                              )
                            },
                            { 
                              key: 'email_verified_at', 
                              label: 'Vérification Email',
                              render: (verified) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  verified ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'
                                }`}>
                                  {verified ? '✓ Vérifié' : '⏳ En attente'}
                                </span>
                              )
                            },
                            { 
                              key: 'status', 
                              label: 'Statut',
                              render: (status) => (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                              )
                            }
                          ]}
                          data={filteredCreators.slice((currentPageCreators - 1) * itemsPerPage, currentPageCreators * itemsPerPage)}
                          onView={handleViewCreator}
                          onEdit={handleEditCreator}
                          onDelete={handleDeleteCreator}
                        />
                        
                        <Pagination
                          currentPage={currentPageCreators}
                          totalPages={totalPagesCreators}
                          onPageChange={setCurrentPageCreators}
                        />
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'orders' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des commandes</h2>
                      <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-white/5 transition-colors text-sm font-medium">
                          <Filter className="w-4 h-4" />
                          Filtrer
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium">
                          <Download className="w-4 h-4" />
                          Exporter
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchOrder}
                        onChange={setSearchOrder}
                        placeholder="Rechercher une commande..."
                      />
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'order_id', label: 'N° Commande' },
                        { key: 'client', label: 'Client' },
                        { key: 'service', label: 'Service' },
                        { 
                          key: 'amount', 
                          label: 'Montant',
                          render: (amount) => `${amount}€`
                        },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => (
                            <span className={`px-2 py-1 rounded text-xs ${
                              status === 'Complété' ? 'bg-green-500/20 text-green-500' :
                              status === 'En cours' ? 'bg-blue-500/20 text-blue-500' :
                              'bg-rose-500/20 text-rose-500'
                            }`}>
                              {status}
                            </span>
                          )
                        },
                        { key: 'date', label: 'Date' }
                      ]}
                      data={filteredOrders.slice((currentPageOrders - 1) * itemsPerPage, currentPageOrders * itemsPerPage)}
                      onView={handleViewOrder}
                      onEdit={null}
                      onDelete={null}
                    />
                    
                    <Pagination
                      currentPage={currentPageOrders}
                      totalPages={totalPagesOrders}
                      onPageChange={setCurrentPageOrders}
                    />
                  </div>
                )}

                {activeTab === 'earnings' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-semibold">Gains par Créateur</h2>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Répartition: 75% créateur • 5% Krayne Agency • 20% URSAFF
                        </p>
                      </div>
                      <button 
                        onClick={() => {
                          // Rafraîchir les données
                          if (activeTab === 'earnings') {
                            const fetchEarnings = async () => {
                              try {
                                const response = await api.get('/admin/earnings');
                                setEarningsData(response.data.earnings || []);
                                setFilteredEarnings(response.data.earnings || []);
                                const totalP = Math.ceil((response.data.earnings || []).length / itemsPerPage);
                                setTotalPagesEarnings(totalP);
                                toast.success('Données rafraîchies');
                              } catch (error) {
                                console.error('Erreur chargement gains:', error);
                                toast.error('Impossible de charger les gains');
                              }
                            };
                            fetchEarnings();
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Rafraîchir
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchEarnings}
                        onChange={setSearchEarnings}
                        placeholder="Rechercher un créateur..."
                      />
                    </div>
                    
                    {/* Tableau des gains */}
                    <div className={`overflow-x-auto rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                            <th className={`text-left p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Créateur</th>
                            <th className={`text-left p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ventes Totales</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Part Créateur (75%)</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Part Admin (5%)</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>URSAFF (20%)</th>
                            <th className={`text-center p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Produits Vendus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEarnings.slice((currentPageEarnings - 1) * itemsPerPage, currentPageEarnings * itemsPerPage).map((earning, idx) => (
                            <tr key={earning.creator_id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                              <td className="p-4">
                                <div className="font-medium">{earning.creator_name}</div>
                                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ID: {earning.creator_id}</div>
                              </td>
                              <td className="p-4 text-sm">{earning.creator_email}</td>
                              <td className="p-4 text-right font-semibold">{earning.total_sales.toFixed(2)} €</td>
                              <td className="p-4 text-right text-green-500 font-medium">{earning.creator_share.toFixed(2)} €</td>
                              <td className="p-4 text-right text-rose-500 font-medium">{earning.admin_share.toFixed(2)} €</td>
                              <td className="p-4 text-right text-orange-500 font-medium">{earning.ursaff_share.toFixed(2)} €</td>
                              <td className="p-4 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                  {earning.products_sold}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {filteredEarnings.length === 0 && (
                            <tr>
                              <td colSpan="7" className="p-8 text-center text-gray-500">
                                Aucune vente enregistrée
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {filteredEarnings.length > 0 && (
                      <Pagination
                        currentPage={currentPageEarnings}
                        totalPages={totalPagesEarnings}
                        onPageChange={setCurrentPageEarnings}
                      />
                    )}
                    
                    {/* Récapitulatif global */}
                    {filteredEarnings.length > 0 && (
                      <div className={`mt-6 p-6 rounded-xl border ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-gray-200'}`}>
                        <h3 className="text-lg font-semibold mb-4">Récapitulatif Global</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Ventes</div>
                            <div className="text-2xl font-bold">{filteredEarnings.reduce((sum, e) => sum + e.total_sales, 0).toFixed(2)} €</div>
                          </div>
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Créateurs (75%)</div>
                            <div className="text-2xl font-bold text-green-500">{filteredEarnings.reduce((sum, e) => sum + e.creator_share, 0).toFixed(2)} €</div>
                          </div>
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Admin (5%)</div>
                            <div className="text-2xl font-bold text-rose-500">{filteredEarnings.reduce((sum, e) => sum + e.admin_share, 0).toFixed(2)} €</div>
                          </div>
                          <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total URSAFF (20%)</div>
                            <div className="text-2xl font-bold text-orange-500">{filteredEarnings.reduce((sum, e) => sum + e.ursaff_share, 0).toFixed(2)} €</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'products' && (
                  <div className="space-y-4">
                    {productsError && (
                      <div className={`p-4 border rounded-xl flex items-start gap-3 shadow-sm ${isDark ? 'bg-gradient-to-r from-rose-500/10 via-orange-500/10 to-amber-500/10 border-white/10' : 'bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 border-amber-100'}`}>
                        <div className="mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isDark ? 'text-rose-300' : 'text-rose-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v3m0 3h.01M10.29 3.86l-7 12A1 1 0 004 18h16a1 1 0 00.87-1.5l-7-12a1 1 0 00-1.74 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="font-semibold text-sm">Impossible de charger les produits</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{productsError}</p>
                          <div className="flex gap-2 pt-2">
                            <button
                              type="button"
                              onClick={fetchProducts}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm ${isDark ? 'bg-rose-500/80 hover:bg-rose-500 text-white' : 'bg-rose-500 hover:bg-rose-600 text-white'}`}
                            >
                              Réessayer
                            </button>
                            <button
                              type="button"
                              onClick={() => setProductsError(null)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${isDark ? 'border-white/10 text-gray-200 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-white'}`}
                            >
                              Masquer
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des produits</h2>
                      <button 
                        onClick={handleCreateProduct}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Nouveau produit
                      </button>
                    </div>
                    
                    {/* Onglets de statut avec compteurs */}
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                      {[
                        { key: 'all', label: 'Tous', count: products.length },
                        { key: 'pending', label: 'En attente', count: products.filter(p => p.status === 'pending').length },
                        { key: 'active', label: 'Actifs', count: products.filter(p => p.status === 'active').length },
                        { key: 'inactive', label: 'Inactifs', count: products.filter(p => p.status === 'inactive').length },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => {
                            setProductStatusTab(tab.key);
                            setCurrentPageProducts(1);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            productStatusTab === tab.key
                              ? 'bg-rose-500 text-white shadow-md'
                              : isDark
                              ? 'bg-white/5 border border-white/10 text-gray-100 hover:bg-white/10'
                              : 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <span>{tab.label}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            productStatusTab === tab.key
                              ? 'bg-white/20 text-white'
                              : isDark
                              ? 'bg-white/10 text-gray-300'
                              : 'bg-white text-gray-600'
                          }`}>
                            {tab.count}
                          </span>
                        </button>
                      ))}
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchProduct}
                        onChange={setSearchProduct}
                        placeholder="Rechercher un produit..."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      <select
                        value={productFilters.creatorId}
                        onChange={(e) => setProductFilters(prev => ({ ...prev, creatorId: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">Tous les créateurs</option>
                        {creatorsList.map((c) => (
                          <option key={c.id_user} value={c.id_user}>
                            {c.nickname || c.fullname || c.email}
                          </option>
                        ))}
                      </select>

                      <select
                        value={productFilters.creatorType}
                        onChange={(e) => setProductFilters(prev => ({ ...prev, creatorType: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">Type de créateur</option>
                        <option value="krayne">Krayne</option>
                        <option value="external">Externe</option>
                      </select>

                      <div className="flex gap-2">
                        <select
                          value={productFilters.saleType}
                          onChange={(e) => setProductFilters(prev => ({ ...prev, saleType: e.target.value }))}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        >
                          <option value="">Type de vente</option>
                          <option value="libre">Libre</option>
                          <option value="unique">Unique</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setProductFilters({ saleType: '', status: '', creatorType: '', creatorId: '' })}
                          className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${isDark ? 'bg-white/5 border border-white/10 text-gray-100 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200'}`}
                        >
                          Réinitialiser
                        </button>
                      </div>
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'id', label: 'ID' },
                        { key: 'name', label: 'Nom' },
                        { 
                          key: 'category', 
                          label: 'Catégorie',
                          render: (cat) => (
                            <span className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                              {cat}
                            </span>
                          )
                        },
                        { 
                          key: 'creator_type', 
                          label: 'Créateur',
                          render: (type, product) => (
                            <div>
                              <span className={`px-2 py-1 rounded text-xs ${
                                type === 'krayne' ? 'bg-rose-500/20 text-rose-500' : 'bg-orange-500/20 text-orange-500'
                              }`}>
                                {type === 'krayne' ? 'Krayne' : 'Externe'}
                              </span>
                              {product.creator_name && (
                                <div className="text-xs text-gray-500 mt-1">{product.creator_name}</div>
                              )}
                            </div>
                          )
                        },
                        { 
                          key: 'price', 
                          label: 'Prix HT',
                          render: (price) => `${price}€`
                        },
                        { 
                          key: 'stock', 
                          label: 'Stock',
                          render: (stock) => stock === 0 ? 'Illimité' : stock
                        },
                        { 
                          key: 'sale_type', 
                          label: 'Type',
                          render: (type) => (
                            <span className={`px-2 py-1 rounded text-xs ${
                              type === 'unique' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                            }`}>
                              {type === 'unique' ? 'Unique' : 'Libre'}
                            </span>
                          )
                        },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => (
                            <span className={`px-2 py-1 rounded text-xs ${
                              status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                            }`}>
                              {status === 'active' ? 'Actif' : 'Inactif'}
                            </span>
                          )
                        },
                        {
                          key: 'actions',
                          label: 'Actions',
                          render: (_, product) => {
                            const isPending = product.status === 'pending';
                            const isActive = product.status === 'active';
                            const powerClass = isActive
                              ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                              : isPending
                                ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                                : isDark ? 'hover:bg-orange-500/10 text-orange-500' : 'hover:bg-orange-50 text-orange-600';

                            const handlePowerClick = () => {
                              if (isPending) return handleActivateProduct(product);
                              return handleToggleProductStatus(product);
                            };

                            return (
                              <div className="flex items-center justify-end gap-2 w-full">
                                <button
                                  onClick={() => handleViewProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-blue-500/10 text-blue-500' : 'hover:bg-blue-50 text-blue-600'}`}
                                  title="Voir"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-yellow-500/10 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-600'}`}
                                  title="Modifier"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handlePowerClick}
                                  className={`p-2 rounded transition ${powerClass}`}
                                  title={isPending ? 'Activer' : isActive ? 'Désactiver' : 'Activer'}
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-red-500/10 text-red-500' : 'hover:bg-red-50 text-red-600'}`}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }
                        },
                        { key: 'created_at', label: 'Date création' }
                      ]}
                      data={filteredProducts.slice((currentPageProducts - 1) * itemsPerPage, currentPageProducts * itemsPerPage)}
                      onView={handleViewProduct}
                      onEdit={handleEditProduct}
                      onDelete={handleDeleteProduct}
                      actions={false}
                    />
                    
                    <Pagination
                      currentPage={currentPageProducts}
                      totalPages={totalPagesProducts}
                      onPageChange={setCurrentPageProducts}
                    />
                  </div>
                )}

                {activeTab === 'messages' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Messages</h2>
                      <button className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium">
                        Tout marquer lu
                      </button>
                    </div>
                    
                    {/* Filtres de type de message */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setMessageTypeFilter('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          messageTypeFilter === 'all'
                            ? 'bg-rose-500 text-white'
                            : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Tous
                      </button>
                      <button
                        onClick={() => setMessageTypeFilter('contact')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          messageTypeFilter === 'contact'
                            ? 'bg-rose-500 text-white'
                            : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Formulaire de contact
                      </button>
                      <button
                        onClick={() => setMessageTypeFilter('conversations')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          messageTypeFilter === 'conversations'
                            ? 'bg-rose-500 text-white'
                            : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Conversation
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchMessage}
                        onChange={setSearchMessage}
                        placeholder="Rechercher un message..."
                      />
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { 
                          key: 'status', 
                          label: '',
                          render: (status) => (
                            <div className={`w-2 h-2 rounded-full ${status === 'nouveau' ? 'bg-rose-500' : 'bg-transparent'}`} />
                          )
                        },
                        { key: 'name', label: 'Nom' },
                        { key: 'email', label: 'Email' },
                        { key: 'subject', label: 'Sujet' },
                        ...(messageTypeFilter !== 'creator_contact' ? [{
                          key: 'request_type',
                          label: 'Type',
                          render: (type) => {
                            const labels = { support: 'Support', creation: 'Création', autre: 'Autre' };
                            const colors = { support: 'blue', creation: 'purple', autre: 'gray' };
                            const label = labels[type] || 'Support';
                            const color = colors[type] || 'blue';
                            return (
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                isDark 
                                  ? `bg-${color}-500/20 text-${color}-400` 
                                  : `bg-${color}-100 text-${color}-700`
                              }`}>
                                {label}
                              </span>
                            );
                          }
                        }] : []),
                        { 
                          key: 'message', 
                          label: 'Message',
                          render: (msg) => {
                            const safe = msg || '';
                            return safe.length > 50 ? safe.substring(0, 50) + '...' : safe;
                          }
                        },
                        { key: 'created_at', label: 'Date' }
                      ]}
                      data={filteredMessages.slice((currentPageMessages - 1) * itemsPerPage, currentPageMessages * itemsPerPage)}
                      onView={handleViewMessage}
                      onEdit={null}
                      onDelete={handleDeleteMessage}
                    />
                    
                    <Pagination
                      currentPage={currentPageMessages}
                      totalPages={totalPagesMessages}
                      onPageChange={setCurrentPageMessages}
                    />
                  </div>
                )}

                {activeTab === 'categories' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des catégories</h2>
                      <button 
                        onClick={() => {
                          setSelectedCategoryData(null);
                          setCategoryFormMode('create');
                          setCategoryModalOpen(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter une catégorie
                      </button>
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'name', label: 'Nom' },
                        { key: 'slug', label: 'Slug' },
                        { 
                          key: 'product_count', 
                          label: 'Produits',
                          render: (_, cat) => cat.products?.length || 0
                        },
                        { 
                          key: 'created_at', 
                          label: 'Créée le',
                          render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-'
                        }
                      ]}
                      data={categories}
                      onEdit={(cat) => {
                        setSelectedCategoryData(cat);
                        setCategoryFormMode('edit');
                        setCategoryModalOpen(true);
                      }}
                      onDelete={(cat) => handleDeleteCategory(cat.id_category)}
                    />
                  </div>
                )}


                {activeTab === 'projects' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des projets</h2>
                      <button 
                        onClick={handleCreateProject}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Créer un projet
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchProject}
                        onChange={setSearchProject}
                        placeholder="Rechercher un projet..."
                      />
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'id_project', label: 'ID' },
                        { key: 'name', label: 'Nom' },
                        { key: 'user_name', label: 'Client' },
                        { key: 'type', label: 'Type' },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              status === 'En cours' ? 'bg-blue-500/20 text-blue-500' :
                              status === 'Terminé' ? 'bg-green-500/20 text-green-500' :
                              'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {status}
                            </span>
                          )
                        },
                        { 
                          key: 'progress', 
                          label: 'Progression',
                          render: (progress) => `${progress || 0}%`
                        }
                      ]}
                      data={filteredProjects.slice((currentPageProjects - 1) * itemsPerPage, currentPageProjects * itemsPerPage)}
                      onView={handleViewProject}
                      onEdit={handleEditProject}
                      onDelete={handleDeleteProject}
                    />
                    
                    <Pagination
                      currentPage={currentPageProjects}
                      totalPages={totalPagesProjects}
                      onPageChange={setCurrentPageProjects}
                    />
                  </div>
                )}

            </div>
            
            {/* Modals */}
            
            {/* Modal Utilisateur */}
            <ModalAdmin
              isOpen={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              title={userFormMode === 'create' ? 'Créer un utilisateur' : userFormMode === 'edit' ? 'Modifier l\'utilisateur' : 'Détails de l\'utilisateur'}
              size="md"
            >
              {userFormMode === 'view' && selectedUser ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.nickname}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prénom</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.firstname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.lastname}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.role || (selectedUser.id_role === 3 ? 'Admin' : selectedUser.id_role === 2 ? 'Créateur' : 'Utilisateur')}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Vérification email</label>
                    <p className={selectedUser.email_verified_at ? 'text-green-500' : 'text-orange-500'}>
                      {selectedUser.email_verified_at ? '✓ Vérifié' : '⏳ En attente'}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte</label>
                    <div className="flex items-center justify-between">
                      <p className={selectedUser.status === 'active' ? 'text-green-500' : 'text-red-500'}>
                        {selectedUser.status === 'active' ? '✓ Actif' : '✕ Inactif'}
                      </p>
                      <button
                        onClick={async () => {
                          const newStatus = selectedUser.status === 'active' ? 'inactive' : 'active';
                          try {
                            await api.put(`/admin/users/${selectedUser.id_user || selectedUser.id}`, {
                              ...selectedUser,
                              status: newStatus
                            });
                            toast.success(`Utilisateur ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
                            setSelectedUser({ ...selectedUser, status: newStatus });
                            fetchUsers();
                          } catch (error) {
                            toast.error('Erreur lors du changement de statut');
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedUser.status === 'active'
                            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                            : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                        }`}
                      >
                        {selectedUser.status === 'active' ? 'Désactiver' : 'Activer'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date d'inscription</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const userData = {
                    nickname: formData.get('nickname'),
                    firstname: formData.get('firstname'),
                    lastname: formData.get('lastname'),
                    email: formData.get('email'),
                    id_role: parseInt(formData.get('role')),
                    status: formData.get('status'),
                  };
                  
                  // Ajouter le mot de passe uniquement si fourni
                  const password = formData.get('password');
                  if (password && password.trim()) {
                    userData.password = password;
                  }
                  
                  handleSaveUser(userData);
                }} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo *</label>
                    <input
                      type="text"
                      name="nickname"
                      defaultValue={selectedUser?.nickname || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prénom *</label>
                      <input
                        type="text"
                        name="firstname"
                        defaultValue={selectedUser?.firstname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom *</label>
                      <input
                        type="text"
                        name="lastname"
                        defaultValue={selectedUser?.lastname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={selectedUser?.email || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    {userFormMode === 'edit' && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        ⚠️ Modifier l'email désactivera le compte jusqu'à vérification
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle *</label>
                    <select
                      name="role"
                      defaultValue={selectedUser?.id_role || 1}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="1">Utilisateur</option>
                      <option value="2">Créateur</option>
                      <option value="3">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte *</label>
                    <select
                      name="status"
                      defaultValue={selectedUser?.status || 'active'}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                    {userFormMode === 'edit' && selectedUser?.status === 'inactive' && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        ⚠️ Cet utilisateur ne pourra pas se connecter
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="password"
                      name="password"
                      required={userFormMode === 'create'}
                      placeholder={userFormMode === 'edit' ? 'Laisser vide pour ne pas modifier' : ''}
                      minLength={8}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Minimum 8 caractères</p>
                  </div>
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setUserModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {userFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              )}
            </ModalAdmin>
            
            {/* Modal Commande */}
            <ModalAdmin
              isOpen={orderModalOpen}
              onClose={() => setOrderModalOpen(false)}
              title="Détails de la commande"
              size="lg"
            >
              {selectedOrder && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>N° Commande</label>
                      <p className="font-semibold">{selectedOrder.order_id}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</label>
                      <p>{selectedOrder.date}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client</label>
                      <p>{selectedOrder.client}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Service</label>
                      <p>{selectedOrder.service}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Montant</label>
                      <p className="text-lg font-semibold text-rose-500">{selectedOrder.amount}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <select
                        defaultValue={selectedOrder.status}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}
                      >
                        <option>Nouveau</option>
                        <option>En cours</option>
                        <option>Complété</option>
                        <option>Annulé</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setOrderModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600">
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Message */}
            <ModalAdmin
              isOpen={messageModalOpen}
              onClose={() => setMessageModalOpen(false)}
              title="Message de contact"
              size="md"
            >
              {selectedMessage && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                    <p>{selectedMessage.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                    <p>{selectedMessage.email}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sujet</label>
                    <p className="font-semibold">{selectedMessage.subject}</p>
                  </div>
                  {selectedMessage.request_type && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de demande</label>
                      <span className={`inline-block px-3 py-1 text-sm rounded-full ${
                        selectedMessage.request_type === 'support' 
                          ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                          : selectedMessage.request_type === 'creation'
                          ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
                          : isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedMessage.request_type === 'support' ? 'Support / Assistance' : selectedMessage.request_type === 'creation' ? 'Demande de création' : 'Autre'}
                      </span>
                    </div>
                  )}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Message</label>
                    <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                      {selectedMessage.message}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Réponse</label>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows="5"
                      className={`w-full px-3 py-2 rounded-lg border resize-none ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      placeholder="Saisissez votre réponse..."
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</label>
                    <p>{selectedMessage.created_at}</p>
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setMessageModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button
                      onClick={handleReplyMessage}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      Répondre
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Produit */}
            <ModalAdmin
              isOpen={productModalOpen}
              onClose={() => {
                setProductModalOpen(false);
                setUploadedImage(null);
                setImagePreview(null);
              }}
              title={productFormMode === 'create' ? 'Créer un produit' : productFormMode === 'edit' ? 'Modifier le produit' : 'Détails du produit'}
              size="lg"
            >
              {productFormMode !== 'view' ? (
                <form 
                  onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleSaveProduct({
                    name: formData.get('name'),
                    category: selectedCategory,
                    price: parseFloat(formData.get('price')),
                    stock: parseInt(formData.get('stock')),
                    sale_type: formData.get('sale_type'),
                    description: formData.get('description'),
                    status: formData.get('status'),
                    creator_type: formData.get('creator_type'),
                    id_user_creator: formData.get('id_user_creator') || null,
                    creator_name: formData.get('creator_name') || null,
                  });
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Nom du produit <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        defaultValue={selectedProduct?.name || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Catégorie <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">-- Sélectionner --</option>
                        {categories.map(cat => (
                          <option key={cat.id_category || cat.id} value={cat.name || cat.name_category}>
                            {cat.name || cat.name_category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prix HT (€) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        defaultValue={selectedProduct?.price || selectedProduct?.price_ht || ''}
                        required
                        onInput={(e) => {
                          const priceHT = parseFloat(e.target.value) || 0;
                          const tva = priceHT * 0.20;
                          const priceTTC = priceHT + tva;
                          
                          // Mettre à jour la div de calcul
                          const priceDiv = document.querySelector('.price-calc-display-admin');
                          if (priceDiv) {
                            priceDiv.innerHTML = `
                              <div class="text-xs space-y-1.5">
                                <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
                                <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
                                <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
                              </div>
                            `;
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Calcul TVA
                      </label>
                      <div className={`price-calc-display-admin w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        <div className="text-xs text-center opacity-50">Saisissez un prix HT</div>
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Type de vente <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="sale_type"
                        value={productSaleType}
                        onChange={(e) => {
                          setProductSaleType(e.target.value);
                          if (e.target.value === 'libre') {
                            setAllowedUsers([]);
                          } else if (e.target.value === 'unique' && allowedUsers.length === 0) {
                            // Pré-sélectionner l'utilisateur créateur du produit si c'est vente unique et personne n'est sélectionné
                            if (selectedProduct?.id_user) {
                              setAllowedUsers([String(selectedProduct.id_user)]);
                            } else if (user && user.id_user) {
                              setAllowedUsers([String(user.id_user)]);
                            }
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="libre">Vente libre</option>
                        <option value="unique">Vente unique</option>
                      </select>
                    </div>
                  </div>

                  {productSaleType === 'unique' && (
                    <div className={`p-5 rounded-lg border-2 ${isDark ? 'border-purple-500/40 bg-gradient-to-br from-purple-500/10 to-purple-500/5' : 'border-purple-300 bg-gradient-to-br from-purple-50 to-purple-25'}`}>
                      {/* Header avec icône */}
                      <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                          Utilisateurs autorisés <span className="text-purple-500">*</span>
                        </h3>
                      </div>

                      {/* Description */}
                      <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span className="font-semibold text-purple-500">⚠️ Vente unique :</span> Sélectionnez UN SEUL utilisateur qui pourra voir et acheter cette création exclusive.
                      </p>

                      {/* Bouton désélectionner */}
                      {allowedUsers.length > 0 && (
                        <div className="mb-4">
                          <button
                            type="button"
                            onClick={() => setAllowedUsers([])}
                            className={`text-xs px-3 py-1.5 rounded font-medium transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                          >
                            ✕ Désélectionner
                          </button>
                        </div>
                      )}

                      {/* Résumé des sélectionnés */}
                      {allowedUsers.length > 0 && (
                        <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                          <p className={`text-sm font-semibold ${isDark ? 'text-purple-200' : 'text-purple-700'}`}>
                            ✓ {allowedUsers.length} utilisateur{allowedUsers.length > 1 ? 's' : ''} sélectionné{allowedUsers.length > 1 ? 's' : ''}
                          </p>
                          <div className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {allowedUsers.map((userId, idx) => {
                              const user = users.find(u => String(u.id_user || u.id) === userId);
                              const label = user ? (user.nickname || `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email) : '';
                              return (
                                <div key={userId} className="text-sm">
                                  • {label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Liste des utilisateurs */}
                      <div className="max-h-60 overflow-y-auto border rounded-lg p-3 pr-2" style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                      }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {users.map((u) => {
                            const id = u.id_user || u.id;
                            const label = u.nickname || `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email;
                            const checked = allowedUsers.includes(String(id));
                            return (
                              <label key={id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                checked 
                                  ? isDark 
                                    ? 'bg-purple-500/15 border-purple-500/50' 
                                    : 'bg-purple-50 border-purple-300'
                                  : isDark
                                    ? 'bg-white/2 border-white/10 hover:bg-white/5'
                                    : 'bg-white/30 border-gray-200 hover:bg-gray-50'
                              }`}>
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  checked
                                    ? isDark
                                      ? 'bg-purple-500/40 border-purple-500'
                                      : 'bg-purple-500 border-purple-500'
                                    : isDark
                                      ? 'border-gray-600'
                                      : 'border-gray-300'
                                }`}>
                                  {checked && (
                                    <svg className={`w-3 h-3 ${isDark ? 'text-purple-200' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <input
                                  type="radio"
                                  name="unique_user"
                                  checked={checked}
                                  onChange={() => {
                                    // Pour une vente unique, un seul utilisateur peut être sélectionné
                                    setAllowedUsers([String(id)]);
                                  }}
                                  className="hidden"
                                  aria-label={label}
                                />
                                {u.photo && (
                                  <img
                                    src={u.photo}
                                    alt={label}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-transparent"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                                    {label}
                                  </p>
                                  {u.email && (
                                    <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                      {u.email}
                                    </p>
                                  )}
                                </div>
                                {checked && (
                                  <div className="flex-shrink-0">
                                    <div className={`text-xs font-semibold px-2 py-1 rounded ${isDark ? 'bg-purple-500/40 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                                      ✓
                                    </div>
                                  </div>
                                )}
                              </label>
                            );
                          })}
                          {users.length === 0 && (
                            <div className={`col-span-2 text-center py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              Aucun utilisateur trouvé
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Avertissement si vide */}
                      {allowedUsers.length === 0 && (
                        <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                          ⚠️ Veuillez sélectionner UN utilisateur pour cette vente unique
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Stock <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="stock"
                        min="0"
                        defaultValue={selectedProduct?.stock ?? '1'}
                        required
                        placeholder="0 = illimité"
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.target.parentElement.querySelector('input[name="stock"]');
                          if (input) {
                            input.value = 0;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                          }
                        }}
                        className={`absolute top-0 right-0 mt-1 mr-1 text-xs px-2 py-1 rounded ${isDark ? 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}
                      >
                        Illimité
                      </button>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        0 = Stock illimité
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Statut <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="status"
                        defaultValue={selectedProduct?.status || 'active'}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows="3"
                      defaultValue={selectedProduct?.description || ''}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Image du produit {productFormMode === 'create' && <span className="text-rose-500">*</span>}
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        required={productFormMode === 'create'}
                        className={`hidden w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          isDark 
                            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        Choisir un fichier
                      </button>
                      {imagePreview ? (
                        <div className="mt-3">
                          <Watermark>
                            <img 
                              src={imagePreview} 
                              alt="Aperçu" 
                              className="max-h-40 rounded-lg mx-auto"
                            />
                          </Watermark>
                        </div>
                      ) : (
                        (() => {
                          const url = resolveProductImageUrl(selectedProduct);
                          return url ? (
                            <div className="mt-3">
                              <Watermark>
                                <img 
                                  src={url}
                                  alt={selectedProduct?.name || 'Image'} 
                                  className="max-h-40 rounded-lg mx-auto"
                                />
                              </Watermark>
                            </div>
                          ) : null;
                        })()
                      )}
                    </div>
                    <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      💡 L'image de création reste conservée pour l'archive même en cas de suppression de produit.
                    </p>
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setProductModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {productFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                      <p className="font-semibold">{selectedProduct?.name}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Catégorie</label>
                      <p>{selectedProduct?.category}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de créateur</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.creator_type === 'krayne' ? 'bg-rose-500/20 text-rose-500' : 'bg-orange-500/20 text-orange-500'
                      }`}>
                        {selectedProduct?.creator_type === 'krayne' ? 'Krayne Agency' : 'Créateur externe'}
                      </span>
                    </div>
                    {selectedProduct?.creator_name && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du créateur</label>
                        <p className="font-semibold">{selectedProduct.creator_name}</p>
                      </div>
                    )}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prix HT</label>
                      <p className="font-semibold">{selectedProduct?.price}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Calcul de prix</label>
                      <div className={`p-2 rounded text-xs ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                        <div>TVA (20%): {(selectedProduct?.price * 0.20).toFixed(2)}€</div>
                        {selectedProduct?.creator_type === 'external' && (
                          <div className="text-rose-500">Commission Krayne (5%): {(selectedProduct?.price * 0.05).toFixed(2)}€</div>
                        )}
                        <div className="font-semibold text-rose-500 mt-1">
                          Prix TTC: {(selectedProduct?.price * (selectedProduct?.creator_type === 'external' ? 1.25 : 1.20)).toFixed(2)}€
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Stock</label>
                      <p>{selectedProduct?.stock}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de vente</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.sale_type === 'unique' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                      }`}>
                        {selectedProduct?.sale_type === 'unique' ? 'Unique' : 'Libre'}
                      </span>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {selectedProduct?.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                  {selectedProduct?.description && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                      <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                        {selectedProduct.description}
                      </p>
                    </div>
                  )}
                  {selectedProduct?.images && selectedProduct.images.length > 0 ? (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Image</label>
                      <Watermark>
                        <img 
                          src={resolveProductImageUrl(selectedProduct)} 
                          alt={selectedProduct?.name} 
                          className="max-h-60 rounded-lg mx-auto"
                        />
                      </Watermark>
                    </div>
                  ) : selectedProduct?.image ? (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Image</label>
                      <Watermark>
                        <img 
                          src={selectedProduct.image} 
                          alt={selectedProduct?.name} 
                          className="max-h-60 rounded-lg mx-auto"
                        />
                      </Watermark>
                    </div>
                  ) : null}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setProductModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button 
                      onClick={() => {
                        setProductFormMode('edit');
                      }}
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Créateur */}
            <ModalAdmin
              isOpen={creatorModalOpen}
              onClose={() => setCreatorModalOpen(false)}
              title={creatorFormMode === 'create' ? 'Créer un créateur' : creatorFormMode === 'edit' ? 'Modifier le créateur' : 'Détails du créateur'}
              size="md"
            >
              {creatorFormMode !== 'view' ? (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleSaveCreator({
                    nickname: formData.get('nickname'),
                    firstname: formData.get('firstname'),
                    lastname: formData.get('lastname'),
                    email: formData.get('email'),
                    password: formData.get('password')
                  });
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Pseudo <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nickname"
                        defaultValue={selectedCreator?.nickname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prénom <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        defaultValue={selectedCreator?.firstname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Nom <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        defaultValue={selectedCreator?.lastname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Email <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={selectedCreator?.email || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Mot de passe {creatorFormMode === 'create' && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      type="password"
                      name="password"
                      placeholder={creatorFormMode === 'edit' ? 'Laisser vide pour ne pas changer' : ''}
                      required={creatorFormMode === 'create'}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {creatorFormMode === 'create' ? 'Minimum 8 caractères' : 'Laisser vide pour conserver le mot de passe actuel'}
                    </p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Photo de profil
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCreatorImageUpload}
                        className={`w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                      />
                      {uploadedCreatorImage && (
                        <div className="mt-3">
                          <p className="text-sm text-green-500">✓ Image sélectionnée: {uploadedCreatorImage.name}</p>
                        </div>
                      )}
                      {selectedCreator?.picture_user && !uploadedCreatorImage && (
                        <div className="mt-3">
                          <img 
                            src={selectedCreator.picture_user} 
                            alt="Aperçu" 
                            className="h-20 w-20 object-cover rounded-full mx-auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setCreatorModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {creatorFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo</label>
                      <p className="font-semibold">{selectedCreator?.nickname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom complet</label>
                      <p>{selectedCreator?.firstname} {selectedCreator?.lastname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                      <p>{selectedCreator?.email}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle</label>
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-500">
                        {selectedCreator?.role}
                      </span>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date d'inscription</label>
                      <p>{selectedCreator?.created_at ? new Date(selectedCreator.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte</label>
                    <div className="flex items-center justify-between">
                      <p className={selectedCreator.status === 'active' ? 'text-green-500' : 'text-red-500'}>
                        {selectedCreator.status === 'active' ? '✓ Actif' : '✕ Inactif'}
                      </p>
                      <button
                        onClick={async () => {
                          const newStatus = selectedCreator.status === 'active' ? 'inactive' : 'active';
                          try {
                            await api.put(`/admin/users/${selectedCreator.id_user || selectedCreator.id}`, {
                              ...selectedCreator,
                              status: newStatus
                            });
                            toast.success(`Créateur ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
                            setSelectedCreator({ ...selectedCreator, status: newStatus });
                            fetchCreators();
                          } catch (error) {
                            toast.error('Erreur lors du changement de statut');
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedCreator.status === 'active'
                            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                            : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                        }`}
                      >
                        {selectedCreator.status === 'active' ? 'Désactiver' : 'Activer'}
                      </button>
                    </div>
                  </div>
                  {selectedCreator?.picture_user && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Photo de profil</label>
                      <img 
                        src={selectedCreator.picture_user} 
                        alt={selectedCreator.nickname} 
                        className="h-32 w-32 object-cover rounded-full mx-auto"
                      />
                    </div>
                  )}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setCreatorModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button 
                      onClick={() => {
                        setCreatorFormMode('edit');
                      }}
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>

            {/* Modal Projet */}
            <ModalAdmin
              isOpen={projectModalOpen}
              onClose={() => setProjectModalOpen(false)}
              title={projectFormMode === 'create' ? 'Créer un projet' : projectFormMode === 'edit' ? 'Modifier le projet' : 'Détails du projet'}
              size="md"
            >
              {projectFormMode === 'view' && selectedProject ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du projet</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.user_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.type || '-'}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.status}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Progression</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.progress}%</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.description || '-'}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const projectData = {
                    id_user: formData.get('id_user'),
                    name: formData.get('name'),
                    type: formData.get('type'),
                    status: formData.get('status'),
                    progress: parseInt(formData.get('progress')) || 0,
                    description: formData.get('description'),
                    start_date: formData.get('start_date') || null,
                    end_date: formData.get('end_date') || null
                  };
                  handleSaveProject(projectData);
                }} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client *</label>
                    <select
                      name="id_user"
                      defaultValue={selectedProject?.id_user || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="">Sélectionnez un client</option>
                      {users.map(u => (
                        <option key={u.id_user} value={u.id_user}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du projet *</label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={selectedProject?.name || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type</label>
                      <input
                        type="text"
                        name="type"
                        defaultValue={selectedProject?.type || ''}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <select
                        name="status"
                        defaultValue={selectedProject?.status || 'En cours'}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="En cours">En cours</option>
                        <option value="En attente">En attente</option>
                        <option value="Terminé">Terminé</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Progression (%)</label>
                    <input
                      type="number"
                      name="progress"
                      min="0"
                      max="100"
                      defaultValue={selectedProject?.progress || 0}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                    <textarea
                      name="description"
                      rows="4"
                      defaultValue={selectedProject?.description || ''}
                      className={`w-full px-3 py-2 rounded-lg border resize-none ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setProjectModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {projectFormMode === 'create' ? 'Créer' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              )}
            </ModalAdmin>

            {/* Modal Catégories */}
            <ModalAdmin
              isOpen={categoryModalOpen}
              onClose={() => {
                setCategoryModalOpen(false);
                setSelectedCategoryData(null);
              }}
              title={categoryFormMode === 'create' ? 'Ajouter une catégorie' : 'Modifier la catégorie'}
            >
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveCategory({
                  name: formData.get('name'),
                  slug: formData.get('slug')
                });
              }} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Nom de la catégorie <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedCategoryData?.name || selectedCategoryData?.name_category || ''}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    placeholder="Ex: Logos, Bannières, Illustrations..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Slug <span className="text-gray-400 text-xs">(optionnel, généré automatiquement si vide)</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    defaultValue={selectedCategoryData?.slug || ''}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    placeholder="Ex: logos, bannieres..."
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Le slug est utilisé dans les URLs. Laissez vide pour génération automatique.
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setCategoryModalOpen(false)}
                    className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                  >
                    {categoryFormMode === 'create' ? 'Créer' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </ModalAdmin>
        </div>
    );
};

export default Dashboard;